<script setup lang="ts">
import Dialog from './Dialog.vue'
import LargeDialog from './LargeDialog.vue'

/**
 * イベント（`event_master` の `type: dialog`）を描画するダイアログ。
 *
 * **器（Dialog / LargeDialog）の選択と、ボタン → 次イベントの配線だけ**を持つ。
 * 本文の描画は `EventBody.vue` にある（#898）。以前は `LargeEventDialog.vue` という
 * コピーが別にあり、そちらだけ video 分岐・評価ボタン（#654）・speaker アイコンの
 * float レイアウトを取りこぼしたまま腐っていた。器が増えても本文は 1 つに保つこと。
 */
const dialogStore = useDialogStore()
const dialog = dialogStore.event

const { triggerEventAndProcess } = useEventSystem()

useMusicScopedReactive(() => dialog.params?.music, () => dialog.visible)

// #898: step の `size` で器を選ぶ。未指定は従来どおり通常サイズ。
const shell = computed(() => (dialog.params?.size === 'large' ? LargeDialog : Dialog))

const runAction = async (button: 'submit' | 'middle' | 'cancel') => {
  dialogStore.closeDialog('event', button)

  const action = dialog.params?.actions[button]
  if (action?.eventId) {
    try {
      await triggerEventAndProcess(action.eventId)
    }
    catch (e) {
      console.error('[EventDialog] Failed to process event:', action.eventId, e)
    }
  }
}

const onSubmit = () => runAction('submit')
const onMiddle = () => runAction('middle')
const onCancel = () => runAction('cancel')
</script>

<template>
  <component
    :is="shell"
    v-if="dialog.params"
    :title="dialog.params.title"
    :model-value="dialog.visible"
    :submit-button="dialog.params.actions.submit ? { title: dialog.params.actions.submit.label, sound: dialog.params.actions.submit.sound, disabled: dialog.params.actions.submit.disabled, effect: dialog.params.actions.submit.effect } : undefined"
    :middle-button="dialog.params.actions.middle ? { title: dialog.params.actions.middle.label, sound: dialog.params.actions.middle.sound, disabled: dialog.params.actions.middle.disabled, effect: dialog.params.actions.middle.effect } : undefined"
    :cancel-button="dialog.params.actions.cancel ? { title: dialog.params.actions.cancel.label, sound: dialog.params.actions.cancel.sound, disabled: dialog.params.actions.cancel.disabled, effect: dialog.params.actions.cancel.effect } : undefined"
    @submit="onSubmit"
    @middle="onMiddle"
    @cancel="onCancel"
  >
    <template #default>
      <DialogEventBody :params="dialog.params" />
    </template>
  </component>
</template>
